/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable react-refresh/only-export-components */
import type { AuthState } from '@/types/backend'
import type { CartDraft, CartItem } from '@/types/cart'
import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type Reducer,
} from 'react'

export const EMPTY_CART: CartDraft = {
  customerId: null,
  items: [],
  tax: 0,
  draft: false,
  initialPaymentAmount: 0,
  initialPaymentMethod: undefined,
  initialOverpayPolicy: 'APPLY_TO_DEBT',
}

export const cartSubTotal = (c: CartDraft) =>
  c.items.reduce((a, it) => a + it.unitPrice * it.quantity, 0)
export const cartTotal = (c: CartDraft) => cartSubTotal(c) + (c.tax ?? 0)
export const cartCanIssue = (c: CartDraft) =>
  c.items.length > 0 && !!c.customerId && !c.draft
export const cartCanSaveDraft = (c: CartDraft) => c.items.length > 0

/*-----State & actions ----------------*/
type AppState = {
  userInfo: AuthState | null
  cart: CartDraft
}

const safeParse = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    if (key === 'cart') {
      return {
        draft: false,
        initialPaymentAmount: 0,
        initialPaymentMethod: undefined,
        initialOverpayPolicy: 'APPLY_TO_DEBT',
        tax: 0,
        customerId: null,
        items: [],
        ...parsed,
      } as T
    }
    return parsed
  } catch {
    return fallback
  }
}

const initialState: AppState = {
  userInfo: safeParse<AuthState | null>('userInfo', null),
  cart: safeParse<CartDraft>('cart', { ...EMPTY_CART }),
}

type Action =
  | { type: 'USER_SIGNIN'; payload: AuthState }
  | { type: 'USER_SIGNUP'; payload: AuthState }
  | { type: 'USER_SIGNOUT' }
  | { type: 'CART_ADD_ITEM'; payload: CartItem }
  | { type: 'CART_UPDATE_ITEM'; index: number; patch: Partial<CartItem> }
  | { type: 'CART_REMOVE_ITEM'; index: number }
  | { type: 'CART_CLEAR' }
  | { type: 'CART_SET_META'; payload: Partial<CartDraft> }

function upsert(items: CartItem[], next: CartItem): CartItem[] {
  if (!next.itemId) return [...items, next]
  const idx = items.findIndex((it) => it.itemId === next.itemId)
  if (idx === -1) return [...items, next]

  const merged: CartItem = {
    ...items[idx],
    quantity: next.quantity ?? items[idx].quantity,
    unitPrice: next.unitPrice ?? items[idx].unitPrice,
    name: next.name ?? items[idx].name,
    category: next.category ?? items[idx].category,
    packaging: next.packaging ?? items[idx].packaging,
  }
  return items.map((it, i) => (i === idx ? merged : it))
}

/*---------Reducers ------------------*/
function userInfoReducer(state: AuthState | null, action: Action) {
  switch (action.type) {
    case 'USER_SIGNIN':
    case 'USER_SIGNUP':
      return action.payload
    case 'USER_SIGNOUT':
      return null
    default:
      return state
  }
}

function cartReducer(state: CartDraft, action: Action) {
  switch (action.type) {
    case 'CART_ADD_ITEM': {
      const items = upsert(state.items, action.payload)
      return { ...state, items }
    }
    case 'CART_UPDATE_ITEM': {
      const items = state.items.map((it, i) =>
        i === action.index ? { ...it, ...action.patch } : it
      )
      return { ...state, items }
    }
    case 'CART_REMOVE_ITEM': {
      const items = state.items.filter((_, i) => i !== action.index)
      return { ...state, items }
    }

    case 'CART_CLEAR':
      return { ...EMPTY_CART }
    case 'CART_SET_META':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

const rootReducer: Reducer<AppState, Action> = (state, action) => {
  if (action.type === 'USER_SIGNOUT') {
    localStorage.removeItem('userInfo')
    localStorage.removeItem('cart')
    return { ...state, userInfo: null, cart: { ...EMPTY_CART } }
  }
  return {
    userInfo: userInfoReducer(state.userInfo, action),
    cart: cartReducer(state.cart, action),
  }
}

/*--------------Context---------------------------*/
type StoreContext = {
  state: AppState
  dispatch: React.Dispatch<Action>
}

const Store = React.createContext<StoreContext | undefined>(undefined)

/*----------------------Provider --------------------- */

function StoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(rootReducer, initialState)

  useEffect(() => {
    try {
      state.userInfo
        ? localStorage.setItem('userInfo', JSON.stringify(state.userInfo))
        : localStorage.removeItem('userInfo')
      localStorage.setItem('cart', JSON.stringify(state.cart))
    } catch {}
  }, [state.userInfo, state.cart])

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch])
  return <Store.Provider value={value}>{children}</Store.Provider>
}

/*---------------Hook d'accès ---------------- */
function useStore() {
  const ctx = useContext(Store)
  if (!ctx) throw new Error('useStore mest be used within <StoreProvider>')
  return ctx
}

export { Store, StoreProvider, useStore }
